import React, { useEffect } from 'react';
import { useAtom } from 'jotai';
import { 
  Menu, 
  MenuButton, 
  MenuList, 
  MenuItem, 
  Button, 
  Flex, 
  Text, 
  Portal, 
  useBreakpointValue,
  Tooltip,
  IconButton,
  Avatar
} from '@chakra-ui/react';
import { FiChevronDown } from 'react-icons/fi';
import { useTranslation } from 'react-i18next';
import { 
  selectedCharacterIdAtom, 
  charactersAtom, 
  fetchCharactersAtom,
  charactersLoadingAtom
} from '../../../atoms/v0/characterAtoms';

/**
 * 角色卡选择器组件
 * 显示一个下拉菜单，允许用户选择不同的角色卡
 */
const CharacterSelector: React.FC = () => {
  const { t } = useTranslation(['v0/chat', 'v0/common']);
  const [characters] = useAtom(charactersAtom);
  const [selectedCharacterId, setSelectedCharacterId] = useAtom(selectedCharacterIdAtom);
  const [, fetchCharacters] = useAtom(fetchCharactersAtom);
  const [isLoading] = useAtom(charactersLoadingAtom);
  
  // 响应式调整
  const menuWidth = useBreakpointValue({ base: '200px', md: '250px' });
  const buttonSize = useBreakpointValue({ base: 'sm', md: 'sm' });
  
  // 组件挂载时加载角色卡列表
  useEffect(() => {
    fetchCharacters();
  }, [fetchCharacters]);
  
  // 当前选择的角色卡
  const selectedCharacter = characters.find(c => c.id === selectedCharacterId);
  const displayName = selectedCharacter?.name || t('v0/chat:defaultCharacter', '默认助手');
  
  // 当没有角色卡数据时显示加载状态
  if (isLoading && characters.length === 0) {
    return (
      <Button
        size={buttonSize}
        variant="outline"
        colorScheme="gray"
        isLoading
        loadingText={t('v0/common:loading', '加载中')}
      >
        {t('v0/chat:characterSelector', '角色选择')}
      </Button>
    );
  }
  
  return (
    <Tooltip 
      label={t('v0/chat:characterSelectorTooltip', '选择AI助手的角色')} 
      placement="bottom"
      hasArrow
    >
      {/* Wrap Menu in a div to allow Tooltip to attach ref */}
      <div> 
        <Menu placement="bottom-end">
          <MenuButton 
            as={Button}
            size="sm"
            variant="outline"
            colorScheme="teal"
            px={2}
            py={1}
            borderRadius="full"
            minW="auto"
          >
            <Flex alignItems="center">
              <Avatar name={displayName} size="xs" mr={1} />
              <FiChevronDown size="12px" />
            </Flex>
          </MenuButton>
          <Portal>
            <MenuList width={menuWidth} zIndex={1500}>
              {characters.map(character => (
                <MenuItem 
                  key={character.id} 
                  onClick={() => setSelectedCharacterId(character.id)}
                  fontWeight={selectedCharacterId === character.id ? 'bold' : 'normal'}
                >
                  <Flex direction="column">
                    <Text>{character.name}</Text>
                    {character.description && (
                      <Text fontSize="xs" color="gray.500">{character.description}</Text>
                    )}
                  </Flex>
                </MenuItem>
              ))}
            </MenuList>
          </Portal>
        </Menu>
      </div>
    </Tooltip>
  );
};

export default CharacterSelector; 